import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, LinearProgress, Paper, Stack,
  TextField, Tooltip, Typography, alpha, useTheme, Divider, Skeleton, Alert,
} from '@mui/material';
import {
  Add, ContentCopy, Delete, Lock, Save, Search, Security, Shield,
  CheckCircle, Tune,
} from '@mui/icons-material';
import api from '../../services/api';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import {
  ADMIN_PAGE_PRIVILEGES,
  getPageActions,
  groupPagePrivilegesBySection,
  pagePermissionKeys,
  privilegeActionMeta,
  type AdminPagePrivilege,
  type PrivilegeActionDef,
} from '../../constants/adminPagePrivileges';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import {
  DEMO_WRITE_BLOCKED_MESSAGE,
  isDemoDashboardWriteProtected,
  notifyDemoWriteBlocked,
} from '../../utils/demoMode';

interface RoleRow {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  permissions: string[];
  isSystem?: boolean;
  isActive?: boolean;
}

function hasPerm(perms: string[], key: string) {
  if (perms.includes('*')) return true;
  if (perms.includes(key)) return true;
  const parts = key.split('.');
  if (parts.length >= 2 && perms.includes(`${parts[0]}.${parts[1]}.*`)) return true;
  if (parts.length >= 1 && perms.includes(`${parts[0]}.*.*`)) return true;
  return false;
}

function stripPageKeys(perms: string[], page: AdminPagePrivilege): string[] {
  const drop = new Set(pagePermissionKeys(page));
  return perms.filter((p) => p !== '*' && !drop.has(p));
}

function pageHasView(perms: string[], page: AdminPagePrivilege): boolean {
  if (hasPerm(perms, page.viewPermission)) return true;
  if (page.managePermission && hasPerm(perms, page.managePermission)) return true;
  return getPageActions(page).some((action) => hasPerm(perms, action.permission));
}

function pageHasAction(perms: string[], page: AdminPagePrivilege, action: PrivilegeActionDef): boolean {
  if (hasPerm(perms, action.permission)) return true;
  if (page.managePermission && hasPerm(perms, page.managePermission)) return true;
  return false;
}

export default function RolesPrivilegesPage() {
  const theme = useTheme();
  const { t } = useI18n();
  const tl = (s: string) => translateUiText(s, t);
  const queryClient = useQueryClient();

  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [draftPerms, setDraftPerms] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [roleDialog, setRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRow | null>(null);
  const [roleForm, setRoleForm] = useState({ name: '', slug: '', description: '' });
  const [dirty, setDirty] = useState(false);
  const [demoBlockedOpen, setDemoBlockedOpen] = useState(false);

  const blockDemoWrite = () => {
    if (!isDemoDashboardWriteProtected()) return false;
    notifyDemoWriteBlocked();
    setDemoBlockedOpen(true);
    return true;
  };

  const { data, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get('/admin/roles')).data,
  });

  const roles: RoleRow[] = data?.data || [];
  const selectedRole = roles.find((r) => r._id === selectedRoleId) || roles[0];

  useEffect(() => {
    if (!roles.length) return;
    const role = roles.find((r) => r._id === selectedRoleId) || roles[0];
    setSelectedRoleId(role._id);
    setDraftPerms([...(role.permissions || [])]);
    setDirty(false);
  }, [roles, selectedRoleId]);

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groupPagePrivilegesBySection()
      .map((s) => ({
        ...s,
        pages: s.pages.filter((p) =>
          !q
          || p.pageLabel.toLowerCase().includes(q)
          || p.path.toLowerCase().includes(q)
          || p.viewPermission.toLowerCase().includes(q)
          || getPageActions(p).some((a) => a.label.toLowerCase().includes(q) || a.permission.toLowerCase().includes(q))
          || (p.description || '').toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.pages.length > 0);
  }, [search]);

  const stats = useMemo(() => {
    const total = ADMIN_PAGE_PRIVILEGES.length;
    let view = 0;
    let write = 0;
    for (const p of ADMIN_PAGE_PRIVILEGES) {
      if (pageHasView(draftPerms, p)) view += 1;
      if (getPageActions(p).some((action) => pageHasAction(draftPerms, p, action))) write += 1;
    }
    const full = draftPerms.includes('*');
    return { total, view, write, full, coverage: full ? 100 : Math.round((view / Math.max(total, 1)) * 100) };
  }, [draftPerms]);

  const savePermsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRole) return null;
      return api.put(`/admin/roles/${selectedRole._id}`, { permissions: draftPerms });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setDirty(false);
    },
  });

  const saveRoleMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: roleForm.name.trim(),
        slug: roleForm.slug.trim().toLowerCase().replace(/\s+/g, '_'),
        description: roleForm.description.trim(),
        permissions: editingRole?.permissions || [],
      };
      if (editingRole?._id) return api.put(`/admin/roles/${editingRole._id}`, payload);
      return api.post('/admin/roles', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setRoleDialog(false);
      setEditingRole(null);
      setRoleForm({ name: '', slug: '', description: '' });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/roles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setSelectedRoleId('');
    },
  });

  const setFullAccess = (on: boolean) => {
    setDraftPerms(on ? ['*'] : []);
    setDirty(true);
  };

  const toggleView = (page: AdminPagePrivilege, on: boolean) => {
    let next = stripPageKeys(draftPerms, page);
    if (on) next.push(page.viewPermission);
    setDraftPerms(Array.from(new Set(next)));
    setDirty(true);
  };

  const toggleAction = (page: AdminPagePrivilege, action: PrivilegeActionDef, on: boolean) => {
    const actions = getPageActions(page);
    let next = stripPageKeys(draftPerms, page);
    next.push(page.viewPermission);
    for (const item of actions) {
      const currentlyOn = pageHasAction(draftPerms, page, item);
      if (item.permission === action.permission ? on : currentlyOn) {
        next.push(item.permission);
      }
    }
    setDraftPerms(Array.from(new Set(next)));
    setDirty(true);
  };

  const toggleSection = (pages: AdminPagePrivilege[], enable: boolean) => {
    let next = draftPerms.filter((p) => p !== '*');
    for (const page of pages) {
      next = stripPageKeys(next, page);
      if (enable) {
        next.push(page.viewPermission);
        for (const action of getPageActions(page)) next.push(action.permission);
      }
    }
    setDraftPerms(Array.from(new Set(next)));
    setDirty(true);
  };

  const unlockPages = useMemo(() => {
    if (draftPerms.includes('*')) return ADMIN_PAGE_PRIVILEGES;
    return ADMIN_PAGE_PRIVILEGES.filter((p) => pageHasView(draftPerms, p));
  }, [draftPerms]);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Configuration"
        title="Roles & Privileges"
        subtitle="Control which admin screens each role can open, with View plus the Add / Edit / Delete options that page actually uses."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                if (blockDemoWrite()) return;
                setEditingRole(null);
                setRoleForm({ name: '', slug: '', description: '' });
                setRoleDialog(true);
              }}
            >
              New Role
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              disabled={!dirty || !selectedRole || savePermsMutation.isPending}
              onClick={() => {
                if (blockDemoWrite()) return;
                savePermsMutation.mutate();
              }}
            >
              Save Privileges
            </Button>
          </Stack>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr) 260px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* ── Role list ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            position: { lg: 'sticky' },
            top: { lg: 16 },
          }}
        >
          <Box sx={{ px: 2, py: 1.75, bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Security color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={800}>{tl('Roles')}</Typography>
              <Chip size="small" label={roles.length} sx={{ ml: 'auto', height: 22 }} />
            </Stack>
          </Box>
          <Box sx={{ maxHeight: { lg: 'calc(100vh - 220px)' }, overflow: 'auto', p: 1 }}>
            {isLoading && [1, 2, 3, 4].map((i) => <Skeleton key={i} height={64} sx={{ mb: 1, borderRadius: 2 }} />)}
            {roles.map((role) => {
              const active = selectedRole?._id === role._id;
              const count = role.permissions.includes('*') ? 'Full' : `${role.permissions.length}`;
              return (
                <Box
                  key={role._id}
                  onClick={() => setSelectedRoleId(role._id)}
                  sx={{
                    p: 1.5,
                    mb: 0.75,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: 1,
                    borderColor: active ? 'primary.main' : 'transparent',
                    bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Shield sx={{ fontSize: 18, color: active ? 'primary.main' : 'text.secondary' }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap>{tl(role.name)}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{role.slug}</Typography>
                    </Box>
                    <Chip size="small" label={count} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                  </Stack>
                  {role.isSystem && (
                    <Chip size="small" label={tl('System')} sx={{ mt: 0.75, height: 20, fontSize: '0.65rem' }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ── Privilege matrix ── */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden', minHeight: 520 }}>
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)}, ${alpha('#0f766e', 0.04)})`,
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>
                  {selectedRole ? tl(selectedRole.name) : tl('Select a role')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tl('Page privileges')} · {stats.coverage}% {tl('coverage')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  placeholder={tl('Search pages…')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <Tooltip title={tl('Grant all screens')}>
                  <Chip
                    icon={<Lock fontSize="small" />}
                    label={tl('Full access')}
                    color={stats.full ? 'primary' : 'default'}
                    variant={stats.full ? 'filled' : 'outlined'}
                    onClick={() => setFullAccess(!stats.full)}
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                {selectedRole && !selectedRole.isSystem && (
                  <Tooltip title={tl('Delete role')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        if (blockDemoWrite()) return;
                        deleteRoleMutation.mutate(selectedRole._id);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {selectedRole && (
                  <Tooltip title={tl('Edit role details')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingRole(selectedRole);
                        setRoleForm({
                          name: selectedRole.name,
                          slug: selectedRole.slug,
                          description: selectedRole.description || '',
                        });
                        setRoleDialog(true);
                      }}
                    >
                      <Tune fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={stats.coverage}
              sx={{ mt: 1.5, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.12) }}
            />
            {dirty && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                {tl('Unsaved privilege changes')}
              </Typography>
            )}
          </Box>

          <Box sx={{ p: { xs: 1.5, md: 2 }, maxHeight: { lg: 'calc(100vh - 280px)' }, overflow: 'auto' }}>
            {sections.map((section) => {
              const sectionEnabled = section.pages.every((p) => pageHasView(draftPerms, p)) || stats.full;
              return (
                <Box key={section.sectionId} sx={{ mb: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: section.sectionAccent }} />
                    <Typography variant="subtitle2" fontWeight={800}>{tl(section.sectionLabel)}</Typography>
                    <Chip size="small" label={`${section.pages.length}`} sx={{ height: 20, fontSize: '0.65rem' }} />
                    <Box flex={1} />
                    <Button
                      size="small"
                      onClick={() => toggleSection(section.pages, !sectionEnabled)}
                      disabled={stats.full}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {sectionEnabled ? tl('Clear section') : tl('Enable section')}
                    </Button>
                  </Stack>

                  <Stack spacing={1}>
                    {section.pages.map((page) => {
                      const actions = getPageActions(page);
                      const viewed = stats.full || pageHasView(draftPerms, page);
                      return (
                        <Box
                          key={page.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                            gap: 1.25,
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2.5,
                            border: 1,
                            borderColor: viewed ? alpha(section.sectionAccent, 0.35) : 'divider',
                            bgcolor: viewed ? alpha(section.sectionAccent, 0.05) : 'background.paper',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <Box minWidth={0}>
                            <Typography variant="body2" fontWeight={700} noWrap>{tl(page.pageLabel)}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {page.path}
                              {page.description ? ` · ${tl(page.description)}` : ''}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
                            <Chip
                              size="small"
                              label={tl('View')}
                              onClick={() => !stats.full && toggleView(page, !viewed)}
                              color={viewed ? 'primary' : 'default'}
                              variant={viewed ? 'filled' : 'outlined'}
                              disabled={stats.full}
                              sx={{ fontWeight: viewed ? 800 : 500, height: 28 }}
                            />
                            {actions.map((action) => {
                              const on = stats.full || pageHasAction(draftPerms, page, action);
                              const meta = privilegeActionMeta(action.kind);
                              return (
                                <Chip
                                  key={action.kind}
                                  size="small"
                                  label={tl(action.label)}
                                  onClick={() => !stats.full && toggleAction(page, action, !on)}
                                  color={on ? meta.color : 'default'}
                                  variant={on ? 'filled' : 'outlined'}
                                  disabled={stats.full}
                                  sx={{ fontWeight: on ? 800 : 500, height: 28 }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ── Summary ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            position: { lg: 'sticky' },
            top: { lg: 16 },
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Box sx={{ px: 2, py: 1.75, bgcolor: alpha('#0f766e', 0.06), borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircle sx={{ fontSize: 18, color: '#0f766e' }} />
              <Typography variant="subtitle2" fontWeight={800}>{tl('Unlocks')}</Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{tl('Screens with View')}</Typography>
                <Typography variant="h5" fontWeight={800}>{stats.full ? stats.total : stats.view}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{tl('Screens with write actions')}</Typography>
                <Typography variant="h5" fontWeight={800}>{stats.full ? stats.total : stats.write}</Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {tl('Nav items this role can open')}
            </Typography>
            <Stack spacing={0.75} sx={{ maxHeight: 360, overflow: 'auto' }}>
              {unlockPages.slice(0, 40).map((p) => (
                <Stack key={p.id} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.sectionAccent || 'primary.main' }} />
                  <Typography variant="caption" fontWeight={600}>{tl(p.pageLabel)}</Typography>
                </Stack>
              ))}
              {!unlockPages.length && (
                <Typography variant="caption" color="text.disabled">{tl('No screens unlocked')}</Typography>
              )}
            </Stack>
            {selectedRole && (
              <Button
                fullWidth
                size="small"
                sx={{ mt: 2 }}
                startIcon={<ContentCopy />}
                onClick={() => {
                  setEditingRole(null);
                  setRoleForm({
                    name: `${selectedRole.name} Copy`,
                    slug: `${selectedRole.slug}_copy`,
                    description: selectedRole.description || '',
                  });
                  setRoleDialog(true);
                }}
              >
                {tl('Clone role')}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={roleDialog} onClose={() => setRoleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRole ? tl('Edit Role') : tl('New Role')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={tl('Name')}
              value={roleForm.name}
              onChange={(e) => setRoleForm((f) => ({
                ...f,
                name: e.target.value,
                slug: editingRole ? f.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
              }))}
              fullWidth
            />
            <TextField
              label={tl('Slug')}
              value={roleForm.slug}
              onChange={(e) => setRoleForm((f) => ({ ...f, slug: e.target.value }))}
              fullWidth
              helperText={tl('Unique code used internally')}
            />
            <TextField
              label={tl('Description')}
              value={roleForm.description}
              onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(false)}>{tl('Cancel')}</Button>
          <Button
            variant="contained"
            disabled={!roleForm.name.trim() || !roleForm.slug.trim() || saveRoleMutation.isPending}
            onClick={() => {
              if (blockDemoWrite()) return;
              saveRoleMutation.mutate();
            }}
          >
            {tl('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={demoBlockedOpen} onClose={() => setDemoBlockedOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tl('Save not allowed')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 0.5 }}>
            {DEMO_WRITE_BLOCKED_MESSAGE}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {tl('Demo tenants cannot change roles or privileges. Only real tenants and Super Admin can save this screen.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDemoBlockedOpen(false)}>{tl('OK')}</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
